import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Pencil, Trash2, Upload, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Field, StatusBadge } from "@/components/common";
import CompanySelect from "@/components/CompanySelect";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, downloadDoc, fmtDate, pretty, isStaffRole, DOC_CATEGORIES, DOC_TYPES, DOC_STATUS } from "@/lib/api";

const Pick = ({ value, onChange, options, testId, none }) => (
  <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{none && <SelectItem value="none">{none}</SelectItem>}{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const ExpiryFields = ({ f, setF }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Document type"><Pick value={f.doc_type} onChange={(v) => setF({ ...f, doc_type: v })} options={DOC_TYPES.map((t) => [t, pretty(t)])} none="—" testId="doc-type-select" /></Field>
    <Field label="Issuing authority"><Input value={f.issuing_authority || ""} onChange={(e) => setF({ ...f, issuing_authority: e.target.value })} data-testid="doc-authority-input" /></Field>
    <Field label="Issue date"><Input type="date" value={f.issue_date || ""} onChange={(e) => setF({ ...f, issue_date: e.target.value })} data-testid="doc-issue-date-input" /></Field>
    <Field label="Expiry date"><Input type="date" value={f.expiry_date || ""} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="doc-expiry-date-input" /></Field>
  </div>
);

export function MetaDialog({ doc, onClose, onDone }) {
  const { isStaff } = useAuth();
  const [f, setF] = useState({ ...doc, warning_days: doc.warning_days || "" });
  const [people, setPeople] = useState([]);
  useEffect(() => { api.get("/users").then((r) => setPeople(r.data.filter((u) => u.status === "active" && (u.tenant_id === doc.tenant_id || isStaffRole(u.role))))).catch(() => {}); }, [doc.tenant_id]);
  const save = async () => {
    const body = { title: f.title, category: f.category, doc_type: f.doc_type || null, issuing_authority: f.issuing_authority || null, issue_date: f.issue_date || null,
      expiry_date: f.expiry_date || null, warning_days: f.warning_days ? Number(f.warning_days) : null, notes: f.notes || null };
    if (f.responsible_user_id) body.responsible_user_id = f.responsible_user_id;
    if (isStaff && f.review_state) body.review_state = f.review_state;
    try { await api.put(`/documents/${doc.id}`, body); toast.success("Document details saved"); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="doc-meta-dialog">
        <DialogHeader><DialogTitle>Document details</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Document name"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="doc-meta-title" /></Field>
          <Field label="Category"><Pick value={f.category} onChange={(v) => setF({ ...f, category: v })} options={DOC_CATEGORIES.map((c) => [c, pretty(c)])} testId="doc-meta-category" /></Field>
          <ExpiryFields f={f} setF={setF} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Warning period (days, optional)"><Input type="number" min={1} value={f.warning_days} onChange={(e) => setF({ ...f, warning_days: e.target.value })} data-testid="doc-warning-days-input" /></Field>
            <Field label="Responsible user"><Pick value={f.responsible_user_id} onChange={(v) => setF({ ...f, responsible_user_id: v })} options={people.map((u) => [u.id, u.name])} none="—" testId="doc-responsible-select" /></Field>
          </div>
          {isStaff && <Field label="Review state (V.A)"><Pick value={f.review_state} onChange={(v) => setF({ ...f, review_state: v })} options={[["pending_verification", "Pending Verification"], ["verified", "Verified"], ["needs_attention", "Needs Attention"]]} testId="doc-review-state-select" /></Field>}
          <Field label="Notes"><Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="doc-notes-input" /></Field>
        </div>
        <DialogFooter><Button onClick={save} data-testid="doc-meta-save-btn">Save details</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UploadDialog({ open, onClose, tenantId, docId, onDone }) {
  const { isStaff } = useAuth();
  const [form, setForm] = useState({ title: "", category: "other", tenant: tenantId || "", doc_type: "", issuing_authority: "", issue_date: "", expiry_date: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      if (docId) await api.post(`/documents/${docId}/versions`, fd);
      else {
        fd.append("title", form.title); fd.append("category", form.category); fd.append("tenant_id", form.tenant || "");
        ["doc_type", "issuing_authority", "issue_date", "expiry_date", "notes"].forEach((k) => fd.append(k, form[k] || ""));
        await api.post("/documents", fd);
      }
      toast.success(docId ? "New version uploaded" : "Document uploaded");
      setFile(null); onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="doc-upload-dialog">
        <DialogHeader><DialogTitle>{docId ? "Upload new version" : "Upload document"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!docId && isStaff && !tenantId && <Field label="Company"><CompanySelect value={form.tenant} onChange={(v) => setForm({ ...form, tenant: v })} testId="doc-upload-company" /></Field>}
          {!docId && <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-upload-title" /></Field>}
          {!docId && (
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="doc-upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{pretty(c)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          {!docId && <ExpiryFields f={form} setF={setForm} />}
          <Field label="File"><Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-upload-file" /></Field>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid="doc-upload-submit">{busy ? "Uploading…" : "Upload"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReviewButtons({ doc, onDone }) {
  const act = async (decision) => {
    let reason = null;
    if (decision !== "approve") {
      reason = window.prompt(decision === "reject" ? "Reason for rejection" : "What needs to be corrected in the replacement?");
      if (!reason) return;
    }
    try { await api.post(`/documents/${doc.id}/review`, { decision, reason }); toast.success("Review recorded"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <span className="mr-1 inline-flex gap-1" data-testid={`doc-review-${doc.id}`}>
      <Button size="sm" variant="outline" className="h-8 text-xs text-emerald-700" onClick={() => act("approve")} data-testid={`doc-approve-${doc.id}`}>Approve</Button>
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => act("request_replacement")} data-testid={`doc-replace-${doc.id}`}>Replace</Button>
      <Button size="sm" variant="outline" className="h-8 text-xs text-red-600" onClick={() => act("reject")} data-testid={`doc-reject-${doc.id}`}>Reject</Button>
    </span>
  );
}

export default function DocumentsPanel({ tenantId }) {
  const { user, isStaff } = useAuth();
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [dialog, setDialog] = useState(null);
  const [meta, setMeta] = useState(null);
  const load = () => api.get("/documents", { params: { tenant_id: tenantId || undefined, category: category === "all" ? undefined : category, status: status === "all" ? undefined : status } }).then((r) => setDocs(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [tenantId, category, status]); // eslint-disable-line
  const remove = async (d) => {
    if (!window.confirm(`Archive "${d.title}"? It stays in the audit trail.`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Document archived"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full sm:w-48" data-testid="doc-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{pretty(c)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-48" data-testid="doc-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.entries(DOC_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        </div>
        <Button onClick={() => setDialog({})} data-testid="doc-upload-open-btn"><Plus className="mr-1 h-4 w-4" /> Upload document</Button>
      </div>
      {!docs.length ? <EmptyState title="No documents yet" hint="Files are stored privately and only accessible to authorised users." testId="docs-empty" /> : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-[820px] text-sm" data-testid="document-vault-table">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-3">Document</th><th className="px-4 py-3">Type / category</th><th className="px-4 py-3">Expiry</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Version</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-t" data-testid={`doc-row-${d.id}`}>
                  <td className="px-4 py-3"><div className="font-medium">{d.title}</div><div className="text-xs text-muted-foreground">{d.versions.at(-1)?.filename} · {d.versions.at(-1)?.uploaded_by_name}{d.issuing_authority ? ` · ${d.issuing_authority}` : ""}{d.responsible_user_name ? ` · owner: ${d.responsible_user_name}` : ""}</div></td>
                  <td className="px-4 py-3 text-xs">{d.doc_type && <div className="font-medium">{pretty(d.doc_type)}</div>}<span className="rounded border px-2 py-0.5">{pretty(d.category)}</span></td>
                  <td className="px-4 py-3 text-xs" data-testid={`doc-expiry-${d.id}`}>{d.expiry_date ? fmtDate(d.expiry_date) : "—"}</td>
                  <td className="px-4 py-3"><StatusBadge kind="doc" status={d.status} testId={`doc-status-${d.id}`} /></td>
                  <td className="px-4 py-3 font-mono text-xs">v{d.current_version}<div className="font-sans text-muted-foreground">{fmtDate(d.updated_at)}</div></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isStaff && d.review_state === "pending_verification" && <ReviewButtons doc={d} onDone={load} />}
                    {isStaff && ["rejected", "needs_attention", "pending_verification"].includes(d.review_state) && <span className="mr-1 inline-block"><WhatsAppButton kind="document" id={d.id} label="WhatsApp" /></span>}
                    {(isStaff || (user.role === "client_owner" && d.review_state !== "verified")) && <Button size="sm" variant="ghost" onClick={() => setMeta(d)} data-testid={`doc-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => downloadDoc(d.id).catch((e) => toast.error(errMsg(e)))} data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setDialog({ docId: d.id })} data-testid={`doc-version-${d.id}`}><Upload className="h-4 w-4" /></Button>
                    {(isStaff || user.role === "client_owner") && <Button size="sm" variant="ghost" onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog && <UploadDialog open onClose={() => setDialog(null)} tenantId={tenantId} docId={dialog.docId} onDone={load} />}
      {meta && <MetaDialog doc={meta} onClose={() => setMeta(null)} onDone={load} />}
    </div>
  );
}
